import React from 'react';
import { DecisionVoucherV2Step } from '../../models/decisionVoucherV2';
import { useStepNavigation } from '../../context';

interface StepInfo {
  key: DecisionVoucherV2Step;
  label: string;
  description: string;
  icon: string;
}

const STEPS: StepInfo[] = [
  {
    key: DecisionVoucherV2Step.QUALIFICATION,
    label: 'Qualification',
    description: 'Qualifiez le dossier pour déterminer l\'éligibilité',
    icon: 'fa-check-square'
  },
  {
    key: DecisionVoucherV2Step.CONFIGURATION,
    label: 'Configuration',
    description: 'Configurez l\'accord ou le refus',
    icon: 'fa-cogs'
  },
  {
    key: DecisionVoucherV2Step.VALIDATION,
    label: 'Validation',
    description: 'Validez et envoyez votre décision',
    icon: 'fa-paper-plane'
  }
];

// Détection des largeurs très étroites (mode ultra-compact)
const isNarrowPanel = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  return element.offsetWidth < 180;
};

const StepNavigation: React.FC = () => {
  const { currentStep, setCurrentStep, isLoading } = useStepNavigation();
  const [isCompact, setIsCompact] = React.useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Détection des redimensionnements
  React.useEffect(() => {
    const checkWidth = () => {
      setIsCompact(isNarrowPanel(containerRef.current));
    };

    // Vérifier la largeur initiale
    checkWidth();

    // Observer les changements de taille
    const observer = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const currentIndex = STEPS.findIndex(step => step.key === currentStep);

  // Mode compact pour les panneaux étroits
  if (isCompact) {
    return (
      <div ref={containerRef} className="tw-flex tw-flex-col tw-w-full">
        <h2 className="tw-text-lg tw-font-semibold tw-mb-4 tw-text-gray-700 tw-text-center">Étapes</h2>

        <div className="tw-space-y-2">
          {STEPS.map((step, index) => {
            // Déterminer l'état de l'étape
            const isActive = step.key === currentStep;
            const isPast = index < currentIndex;

            return (
              <div
                key={step.key}
                className={`tw-flex tw-flex-col tw-items-center tw-p-2 tw-rounded-lg tw-transition-all tw-duration-200 tw-cursor-pointer
                  ${isActive ? 'tw-bg-blue-50 tw-border-l-4 tw-border-blue-500' : 'tw-bg-white'}
                  ${isPast ? 'tw-opacity-80 hover:tw-opacity-100' : 'tw-opacity-60 hover:tw-opacity-80'}
                `}
                onClick={() => !isLoading && setCurrentStep(step.key)}
                title={`${step.label}: ${step.description}`}
              >
                <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-1
                  ${isActive ? 'tw-bg-blue-500 tw-text-white' :
                  isPast ? 'tw-bg-green-100 tw-text-green-700 tw-border tw-border-green-300' :
                  'tw-bg-gray-100 tw-text-gray-500 tw-border tw-border-gray-300'}`}
                >
                  {isPast ? (
                    <i className="fas fa-check tw-text-xs"></i>
                  ) : (
                    <i className={`fas ${step.icon} tw-text-xs`}></i>
                  )}
                </div>
                <span className={`tw-text-xs tw-font-medium ${isActive ? 'tw-text-blue-700' : 'tw-text-gray-700'}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Mode normal pour les largeurs standard
  return (
    <div ref={containerRef} className="tw-flex tw-flex-col tw-w-full">
      <h2 className="tw-text-lg tw-font-semibold tw-mb-6 tw-text-gray-700">Étapes</h2>

      <div className="tw-space-y-4">
        {STEPS.map((step, index) => {
          // Déterminer l'état de l'étape
          const isActive = step.key === currentStep;
          const isPast = index < currentIndex;
          const isFuture = index > currentIndex;

          return (
            <div
              key={step.key}
              className={`tw-relative tw-flex tw-flex-col tw-p-4 tw-rounded-lg tw-transition-all tw-duration-200 tw-cursor-pointer
                ${isActive ? 'tw-bg-blue-50 tw-border-l-4 tw-border-blue-500' : 'tw-bg-white'}
                ${isPast ? 'tw-opacity-80 hover:tw-opacity-100' : ''}
                ${isFuture ? 'tw-opacity-60 hover:tw-opacity-80' : ''}
              `}
              onClick={() => !isLoading && setCurrentStep(step.key)}
            >
              {/* Indicateur d'étape */}
              <div className="tw-flex tw-items-center tw-mb-2">
                <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-3
                  ${isActive ? 'tw-bg-blue-500 tw-text-white' :
                   isPast ? 'tw-bg-green-100 tw-text-green-700 tw-border tw-border-green-300' :
                   'tw-bg-gray-100 tw-text-gray-500 tw-border tw-border-gray-300'}`}
                >
                  {isPast ? (
                    <svg className="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <h3 className={`tw-font-medium ${isActive ? 'tw-text-blue-700' : 'tw-text-gray-700'}`}>
                  {step.label}
                </h3>
              </div>

              {/* Description de l'étape */}
              <p className="tw-text-sm tw-text-gray-600 tw-ml-11">{step.description}</p>

              {/* Ligne verticale de connexion */}
              {index < STEPS.length - 1 && (
                <div className="tw-absolute tw-left-8 tw-top-12 tw-w-0.5 tw-h-10 tw-bg-gray-200 tw--z-10"></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Composant séparé pour les boutons de navigation
export const NavigationButtons: React.FC = () => {
  const { currentStep, goToNextStep, goToPreviousStep, isLoading } = useStepNavigation();
  const [isCompact, setIsCompact] = React.useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const currentIndex = STEPS.findIndex(step => step.key === currentStep);

  // Détection des redimensionnements
  React.useEffect(() => {
    const checkWidth = () => {
      setIsCompact(isNarrowPanel(containerRef.current));
    };

    // Vérifier la largeur initiale
    checkWidth();

    // Observer les changements de taille
    const observer = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="tw-flex tw-justify-between tw-bg-white">
      {isCompact ? (
        // Mode compact pour les boutons
        <>
          <button
            className={`tw-p-2 tw-rounded tw-border tw-text-sm tw-font-medium tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-blue-500
              ${currentIndex > 0
              ? 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-50'
              : 'tw-bg-gray-100 tw-text-gray-400 tw-border-gray-200 tw-cursor-not-allowed'}`}
            onClick={goToPreviousStep}
            disabled={currentIndex === 0 || isLoading}
            title="Précédent"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            className={`tw-p-2 tw-rounded tw-text-sm tw-font-medium tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-blue-500
              ${currentIndex < STEPS.length - 1
              ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700'
              : 'tw-bg-gray-100 tw-text-gray-400 tw-border tw-border-gray-200 tw-cursor-not-allowed'}`}
            onClick={goToNextStep}
            disabled={currentIndex === STEPS.length - 1 || isLoading}
            title="Suivant"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </>
      ) : (
        // Mode normal
        <>
          <button
            className={`tw-px-4 tw-py-2 tw-rounded tw-border tw-text-sm tw-font-medium tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-blue-500
              ${currentIndex > 0
              ? 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-50'
              : 'tw-bg-gray-100 tw-text-gray-400 tw-border-gray-200 tw-cursor-not-allowed'}`}
            onClick={goToPreviousStep}
            disabled={currentIndex === 0 || isLoading}
          >
            Précédent
          </button>

          <button
            className={`tw-px-4 tw-py-2 tw-rounded tw-text-sm tw-font-medium tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-blue-500
              ${currentIndex < STEPS.length - 1
              ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700'
              : 'tw-bg-gray-100 tw-text-gray-400 tw-border tw-border-gray-200 tw-cursor-not-allowed'}`}
            onClick={goToNextStep}
            disabled={currentIndex === STEPS.length - 1 || isLoading}
          >
            Suivant
          </button>
        </>
      )}
    </div>
  );
};

export default StepNavigation;